(() => {
  if (customElements.get('everactiv-delivery')) return;

  const preferenceKey = 'everactiv.delivery.preference.v1';
  const cacheKey = 'everactiv.delivery.estimates.v1';
  const cooldownKey = 'everactiv.delivery.cooldowns.v1';
  const preferenceTTL = 30 * 24 * 60 * 60 * 1000;
  const estimateTTL = 10 * 60 * 1000;
  const validPincode = value => typeof value === 'string' && /^[1-9][0-9]{5}$/.test(value);
  const memory = new Map();
  const listeners = new Set();
  const pending = new Map();
  let instance = 0;
  let storageBlocked = false;

  function read(key) {
    if (storageBlocked) return memory.get(key) ?? null;
    let raw;
    try {
      raw = localStorage.getItem(key);
    } catch { storageBlocked = true; return memory.get(key) ?? null; }
    try {
      const value = raw ? JSON.parse(raw) : null;
      memory.set(key, value);
      return value;
    } catch { write(key, null); return null; }
  }

  function write(key, value) {
    memory.set(key, value);
    if (storageBlocked) return;
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch { storageBlocked = true; }
  }

  function preference() {
    const value = read(preferenceKey);
    if (value?.version === 1 && validPincode(value.pincode) && Number.isFinite(value.expiresAt) && value.expiresAt > Date.now()) return value;
    if (value !== null) write(preferenceKey, null);
    return null;
  }

  let currentPincode = preference()?.pincode ?? '';
  function synchronize() {
    const next = preference()?.pincode ?? '';
    if (next !== currentPincode) {
      pending.forEach(request => request.controller.abort());
      pending.clear();
      currentPincode = next;
    }
    listeners.forEach(listener => listener());
  }

  function save(pincode) {
    write(preferenceKey, { version: 1, pincode, expiresAt: Date.now() + preferenceTTL });
    synchronize();
  }

  const routeKey = (endpoint, pincode) => JSON.stringify([endpoint, pincode]);
  function entries(key) {
    const values = read(key);
    return Array.isArray(values) ? values : [];
  }

  function cached(endpoint, pincode) {
    return entries(cacheKey).find(item => item?.key === routeKey(endpoint, pincode) &&
      Number.isSafeInteger(item.days) && item.days >= 0 && Number.isFinite(item.at) &&
      item.at <= Date.now() && Date.now() - item.at < estimateTTL);
  }

  function cooldown(endpoint) {
    const entry = entries(cooldownKey).find(item => item?.endpoint === endpoint && Number.isFinite(item.until));
    return entry?.until > Date.now() ? entry.until : 0;
  }

  async function lookup(endpoint, pincode) {
    const hit = cached(endpoint, pincode);
    if (hit) return { state: 'success', days: hit.days };
    const until = cooldown(endpoint);
    if (until) return { state: 'rateLimited', until };
    const key = routeKey(endpoint, pincode);
    if (pending.has(key)) return pending.get(key).promise;
    const request = { controller: new AbortController() };
    pending.set(key, request);
    request.promise = (async () => {
      const timeout = setTimeout(() => request.controller.abort(), 12000);
      try {
        const url = new URL(endpoint, location.origin);
        url.searchParams.set('ship_to', pincode);
        const response = await fetch(url, {
          credentials: 'omit', headers: { Accept: 'application/json' },
          signal: request.controller.signal, referrerPolicy: 'no-referrer'
        });
        if (response.status === 429) {
          const header = response.headers.get('Retry-After');
          const seconds = header?.trim() ? Number(header) : NaN;
          const date = header ? Date.parse(header) : NaN;
          const until = Number.isFinite(seconds) && seconds >= 0 ? Date.now() + seconds * 1000 :
            Number.isFinite(date) && date > Date.now() ? date : Date.now() + 60000;
          write(cooldownKey, [...entries(cooldownKey).filter(item => item?.endpoint !== endpoint && item?.until > Date.now()), { endpoint, until }]);
          return { state: 'rateLimited', until };
        }
        if (!response.ok && response.status !== 404) throw new Error('Lookup unavailable');
        const data = await response.json();
        if (request.controller.signal.aborted) return { state: 'unavailable' };
        if (response.status === 404 && data?.matched === false && data.working_days === null) return { state: 'missing' };
        if (!response.ok || !Number.isSafeInteger(data) || data < 0) throw new Error('Invalid estimate');
        const fresh = entries(cacheKey).filter(item => item?.key !== key && Number.isFinite(item?.at) && Date.now() - item.at < estimateTTL);
        write(cacheKey, [...fresh, { key, days: data, at: Date.now() }].slice(-12));
        return { state: 'success', days: data };
      } catch { return { state: 'unavailable' }; }
      finally {
        clearTimeout(timeout);
        if (pending.get(key) === request) pending.delete(key);
      }
    })();
    return request.promise;
  }

  window.addEventListener('storage', event => {
    if (event.key === null || [preferenceKey, cacheKey, cooldownKey].includes(event.key)) synchronize();
  });
  window.addEventListener('pageshow', synchronize);
  window.addEventListener('focus', synchronize);

  class EveractivDelivery extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.events) return;
        this.input = this.querySelector('input');
        this.status = this.querySelector('[role="status"]');
        this.editor = this.querySelector('[data-editor]');
        this.summary = this.querySelector('[data-summary]');
        if (!this.input || !this.status || !this.editor || !this.summary) return;
        this.account = this.dataset.context === 'account';
        const id = `delivery-preference-${++instance}`;
        this.input.id = id;
        this.querySelector('label').htmlFor = id;
        this.status.id = `${id}-status`;
        this.input.setAttribute('aria-describedby', this.status.id);
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.addEventListener('click', event => {
          const button = event.target.closest('button[data-action]');
          if (!button || !this.contains(button)) return;
          event.preventDefault();
          event.stopPropagation();
          this.action(button.dataset.action);
        }, options);
        this.input.addEventListener('keydown', event => {
          if (event.key !== 'Enter' && event.key !== 'Escape') return;
          event.preventDefault();
          event.stopPropagation();
          this.action(event.key === 'Enter' ? 'save' : 'cancel');
        }, options);
        this.input.addEventListener('input', () => {
          this.input.removeAttribute('aria-invalid');
          this.status.textContent = '';
        }, options);
        this.onState = () => this.sync();
        listeners.add(this.onState);
        this.hidden = false;
        this.sync();
      });
    }

    disconnectedCallback() {
      this.revision = (this.revision || 0) + 1;
      this.events?.abort();
      this.events = null;
      listeners.delete(this.onState);
      clearTimeout(this.retryTimer);
    }

    sync() {
      const saved = preference()?.pincode ?? '';
      if (saved !== this.pincode) {
        this.pincode = saved;
        this.editing = !saved && !this.account;
        this.result = null;
        this.input.value = saved;
        this.input.removeAttribute('aria-invalid');
      }
      this.render();
      if (!this.account && saved && !this.editing && (!this.result || cached(this.dataset.endpoint, saved) || this.result.state === 'success')) this.refresh();
    }

    render() {
      this.editor.hidden = !this.editing;
      this.summary.hidden = this.editing || !this.pincode;
      this.querySelector('[data-empty]').hidden = !!this.pincode || this.editing;
      this.querySelector('[data-pincode]').textContent = this.pincode || '';
      this.querySelector('[data-action="cancel"]').hidden = !this.pincode && !this.account;
      this.querySelector('[data-heading]').hidden = !this.account && !!this.pincode && !this.editing;
      this.show(this.editing ? '' : this.result?.state || '', this.editing ? null : this.result);
    }

    show(state, result) {
      this.dataset.state = state || 'idle';
      if (state === 'loading') this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      this.status.textContent = state === 'success' ?
        (result.days === 1 ? this.dataset.resultOne : this.dataset.resultOther).replace('__days__', String(result.days)) :
        this.dataset[state] || '';
      const retry = this.querySelector('[data-action="retry"]');
      retry.hidden = this.account || !['missing', 'unavailable', 'rateLimited'].includes(state);
      retry.disabled = state === 'rateLimited' && result.until > Date.now();
      clearTimeout(this.retryTimer);
      if (retry.disabled) this.retryTimer = setTimeout(() => { retry.disabled = false; }, Math.min(result.until - Date.now(), 2147483647));
    }

    async refresh() {
      const pincode = this.pincode;
      const revision = this.revision = (this.revision || 0) + 1;
      const hit = cached(this.dataset.endpoint, pincode);
      if (!hit) this.show('loading');
      const result = await lookup(this.dataset.endpoint, pincode);
      if (!this.isConnected || this.revision !== revision || this.pincode !== pincode || this.editing) return;
      this.result = result;
      this.show(result.state, result);
    }

    action(action) {
      if (action === 'change') {
        this.editing = true;
        this.revision = (this.revision || 0) + 1;
        this.input.value = this.pincode || '';
        this.render();
        this.input.focus();
      } else if (action === 'cancel') {
        this.input.removeAttribute('aria-invalid');
        this.editing = !this.pincode && !this.account;
        this.render();
        this.sync();
        if (!this.pincode && !this.account) this.input.focus();
        else this.querySelector(this.pincode ? '[data-summary] [data-action="change"]' : '[data-empty] [data-action="change"]').focus();
      } else if (action === 'save') {
        const pincode = this.input.value.trim();
        if (!validPincode(pincode)) {
          this.input.setAttribute('aria-invalid', 'true');
          this.show('invalid');
          this.input.focus();
          return;
        }
        this.input.removeAttribute('aria-invalid');
        this.editing = false;
        this.result = null;
        save(pincode);
        this.querySelector('[data-summary] [data-action="change"]').focus();
      } else if (action === 'remove') {
        write(preferenceKey, null);
        write(cacheKey, null);
        synchronize();
        this.querySelector('[data-empty] [data-action="change"]').focus();
      } else if (action === 'retry' && !cooldown(this.dataset.endpoint)) {
        this.refresh();
      }
    }
  }

  customElements.define('everactiv-delivery', EveractivDelivery);
})();
